import { useState } from "react";
import type { Role } from "@hopitech/contracts";
import { destinationConnexion } from "../lib/navigation.ts";
import { useRoute } from "../lib/router.tsx";
import { ErreurApi, lienMagique } from "../lib/api.ts";
import { EDITIONS, ROLES_UI, accueilRole } from "../app/config.ts";
import { useApp } from "../app/state.tsx";

/**
 * Connexion. En mode « better-auth » : e-mail + mot de passe (ou création, ou lien magique),
 * le rôle vient des rattachements du serveur. En mode « stub » (dév) : bootstrap avec un
 * sélecteur de profil pour la maquette.
 */
export function Login(): React.ReactElement {
  const { naviguer } = useRoute();
  const app = useApp();
  const reel = app.mode === "better-auth";

  const [email, setEmail] = useState("");
  const [motDePasse, setMotDePasse] = useState("");
  const [nom, setNom] = useState("");
  const [role, setRole] = useState<Role>("exposant");
  const [vue, setVue] = useState<"connexion" | "inscription" | "magic">("connexion");
  const [erreur, setErreur] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);

  async function soumettre(): Promise<void> {
    setErreur(null);
    setEnCours(true);
    try {
      if (!reel) {
        await app.connecter({ compte: email || "demo", role });
        naviguer(destinationConnexion(window.location.pathname + window.location.search + window.location.hash, accueilRole(role)));
      } else if (vue === "inscription") {
        await app.inscrire(email, motDePasse, nom || email);
        naviguer(destinationConnexion(window.location.pathname + window.location.search + window.location.hash));
      } else if (vue === "magic") {
        await lienMagique(email, destinationConnexion(window.location.pathname + window.location.search + window.location.hash));
        setVue("magic-envoye" as never);
      } else {
        await app.connecterEmail(email, motDePasse);
        naviguer(destinationConnexion(window.location.pathname + window.location.search + window.location.hash));
      }
    } catch (e) {
      setErreur(e instanceof ErreurApi ? e.message : "Action impossible. Réessayez.");
    } finally {
      setEnCours(false);
    }
  }

  const magicEnvoye = (vue as string) === "magic-envoye";

  return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", padding: 24, background: "var(--paper)" }}>
      <div className="hopi-in" style={{ display: "grid", gridTemplateColumns: "minmax(320px,400px) minmax(0,360px)", gap: 24, maxWidth: 800, width: "100%" }}>
        <div className="card" style={{ padding: "28px 26px", display: "flex", flexDirection: "column", gap: 18 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 11 }}>
            <div className="display" style={{ width: 34, height: 34, borderRadius: 9, background: "var(--accent)", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800 }}>H</div>
            <div>
              <div className="display" style={{ fontSize: 18, color: "var(--accent)", lineHeight: 1 }}>Hopitech</div>
              <div className="eyebrow">{reel ? "Espace inscription" : "Espace inscription · démo"}</div>
            </div>
          </div>

          {magicEnvoye ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <h1 className="display" style={{ fontSize: 20, margin: 0 }}>Vérifiez votre boîte mail</h1>
              <p style={{ color: "var(--muted)", margin: 0 }}>Un lien de connexion à usage unique a été envoyé à <strong>{email}</strong>. Il expire dans 15 minutes.</p>
              <button className="btn btn-ghost" onClick={() => setVue("connexion")}>← Revenir à la connexion</button>
            </div>
          ) : (
            <>
              <div>
                <h1 className="display" style={{ fontSize: 22, margin: "0 0 4px" }}>
                  {vue === "inscription" ? "Créer un compte" : "Connexion"}
                </h1>
                <p style={{ color: "var(--muted)", margin: 0, fontSize: 13 }}>
                  {reel ? "Accédez à votre espace pour l'édition en cours." : "Mode démo : choisissez un profil pour entrer."}
                </p>
              </div>

              {erreur && <div className="pill pill-danger" style={{ alignSelf: "flex-start", textTransform: "none", fontFamily: "var(--sans)", fontSize: 12.5 }}>{erreur}</div>}

              {reel && vue === "inscription" && (
                <div className="field">
                  <label htmlFor="nom">Nom</label>
                  <input id="nom" className="input" value={nom} onChange={(e) => setNom(e.target.value)} placeholder="Votre nom" />
                </div>
              )}

              <div className="field">
                <label htmlFor="email">Adresse e-mail</label>
                <input id="email" className="input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="vous@etablissement.fr" />
              </div>

              {reel && vue !== "magic" && (
                <div className="field">
                  <label htmlFor="mdp">Mot de passe</label>
                  <input id="mdp" className="input" type="password" value={motDePasse} onChange={(e) => setMotDePasse(e.target.value)} placeholder="••••••••" />
                </div>
              )}

              {!reel && (
                <div className="field">
                  <label htmlFor="role">Entrer en tant que</label>
                  <select id="role" className="select" value={role} onChange={(e) => setRole(e.target.value as Role)}>
                    {ROLES_UI.map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}
                  </select>
                </div>
              )}

              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                <button className="btn btn-primary" disabled={enCours} onClick={() => void soumettre()}>
                  {enCours ? "…" : vue === "inscription" ? "Créer mon compte" : vue === "magic" ? "Envoyer le lien" : "Se connecter"}
                </button>
                {reel && (
                  <div style={{ display: "flex", gap: 8 }}>
                    <button className="btn btn-ghost" style={{ flex: 1 }} onClick={() => setVue(vue === "magic" ? "connexion" : "magic")}>
                      {vue === "magic" ? "← Mot de passe" : "Lien de connexion"}
                    </button>
                    <button className="btn btn-ghost" style={{ flex: 1 }} onClick={() => setVue(vue === "inscription" ? "connexion" : "inscription")}>
                      {vue === "inscription" ? "← Me connecter" : "Créer un compte"}
                    </button>
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        <div className="card" style={{ padding: "28px 24px", background: "var(--accent)", color: "#fff", border: 0, display: "flex", flexDirection: "column", justifyContent: "space-between", gap: 20 }}>
          <div>
            <div style={{ opacity: 0.8, fontSize: 11, letterSpacing: "0.09em", textTransform: "uppercase", fontWeight: 700 }}>{EDITIONS[0]?.optionLabel}</div>
            <h2 className="display" style={{ fontSize: 26, margin: "8px 0 10px", color: "#fff" }}>Le congrès de l'ingénierie hospitalière</h2>
            <p style={{ opacity: 0.85, margin: 0, fontSize: 13.5, lineHeight: 1.5 }}>Exposants, stagiaires, intervenants et visiteurs — un seul espace pour s'inscrire, gérer sa présence et retrouver ses documents.</p>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {["Exposer", "Se former", "Visiter"].map((t) => (
                <span key={t} style={{ fontSize: 12, fontWeight: 600, padding: "5px 11px", borderRadius: 999, background: "rgba(255,255,255,.15)" }}>{t}</span>
              ))}
            </div>
            <button onClick={() => naviguer("/public/inscription/visiteur")} style={{ alignSelf: "flex-start", background: "#fff", color: "var(--accent)", border: 0, borderRadius: 9, padding: "9px 15px", fontSize: 13.5, fontWeight: 600, cursor: "pointer" }}>Préinscription visiteur →</button>
          </div>
        </div>
      </div>
    </div>
  );
}
